import {memo, useContext, useMemo} from 'react';

import {without} from 'lodash';
import styled, {css} from 'styled-components';
import {useApiIsLoaded} from '@vis.gl/react-google-maps';

import {AddressViewValuesMap} from '~/shared/consts/addressConsts';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {getLocalizationService} from '~/shared/services/localisationService';
import {findAddressByAddressKey} from '~/shared/utils/address';
import {DropdownContainer} from '~/shared/components/DropdownButton';
import {media} from '~/shared/theme/media';
import {flipOnLTR, rotateOnRTL} from '~/shared/theme/flipOnLTR';
import BackArrow from '~/assets/images/svgIconsAsText/back-arrow.svg?react';
import {flexColumn, flexCenter, flexCenterVertically} from '~/shared/theme/FlexLayout';
import NewLocationIcon from '~/assets/images/icons/location-icon.svg?react';
import MobileStandaloneHeader from '~/shared/components/MobileStandaloneHeader/MobileStandaloneHeader';

import MainBlock from './MainBlock';
import ButtonText from './ButtonText';
import AddCompanyAddressBlock from './AddCompanyAddressBlock';
import EditRemoteAddressBlock from './EditRemoteAddressBlock';
import UpsertLocalAddressBlock from './UpsertLocalAddressBlock';
import TitleText from './AddressDropdownTitleText';
import {AddressDropdownContext} from './context';

const addressStandaloneTitlesMap: Record<AddressViewValuesMap, string> = {
  [AddressViewValuesMap.MAIN]: 'addressDropdownStandaloneTitle',
  [AddressViewValuesMap.ADD]: 'add_new_address',
  [AddressViewValuesMap.ADD_COMPANY_ADDRESS]: 'add_company_address',
  [AddressViewValuesMap.EDIT]: 'edit_address',
  [AddressViewValuesMap.EDIT_LOCAL]: 'edit_address',
};

// Z-index is required to bump the MobileStandaloneHeader above the regular header
const Root = styled.div<{isOpen: boolean}>`
  transition: all 0.1s ease-in-out;
  ${({theme, isOpen}) => `
    z-index: ${isOpen ? theme.zIndex.header : theme.zIndex.languageChangeButton};
    flex: 1;
    ${flexCenterVertically}
  `}
  ${media.minLargeMobile`
    z-index: unset;
  `}
`;

const contentWrapperNewStyles = css`
  position: fixed;
  inset: 0;
  height: auto;
`;

const contentWrapperDesktopStyle = css`
  position: absolute;
  width: 400px;
  height: 560px;
  max-height: calc(100vh - ${({theme}) => theme.header.height + theme.subHeader.height}px);
  border-radius: 12px;
  ${flipOnLTR`
    left: auto;
  `}
`;

const AddressContentWrapper = styled.div`
  background-color: ${({theme}) => theme.modal.backgroundColor};
  box-shadow: ${({theme}) => theme.shadows.shadow1};
  border-radius: 0;
  z-index: 99;
  overflow: hidden;
  height: calc(100% - 120px);
  ${contentWrapperNewStyles}
  ${media.minLargeMobile`
    ${contentWrapperDesktopStyle}
  `}
`;

const AddressContentBody = styled.div`
  height: 100%;
  ${flexColumn}
  ${media.minTablet`
    position: relative;
  `};
`;

const Warning = styled.div`
  height: 40px;
  ${flexCenter};
  background-color: ${({theme}) => theme.colors.error};
  color: ${({theme}) => theme.colors.primaryText};
`;

const CloseAddress = styled(BackArrow)`
  position: absolute;
  ${flipOnLTR`
    right: 4px;
  `}
  ${rotateOnRTL}
  display: ${({open}) => (open ? 'block' : 'none')};
`;

const StyledDropdownContainer = styled(DropdownContainer)`
  button {
    border: none;
    justify-content: flex-start;
  }
`;

const StyledMobileStandaloneHeader = styled(MobileStandaloneHeader)`
  background-color: ${({theme}) => theme.colors.surface};
  z-index: ${({theme}) => theme.zIndex.header};
`;

const AddressDropdown = () => {
  const isGoogleMapReady = useApiIsLoaded();
  const {t} = getLocalizationService();
  const {
    deliveryMethod,
    addressKey,
    initialAddresses,
    userData,
    addressView,
    editedAddress,
    onSubmitEditedAddress,
    handleSubmitLocalAddress,
    isMaxLargeMobile,
    isMaxMobile,
    toggleDropdown,
    isMinLargeMobile,
    isMenuPage,
    handleAddCompanyAddress,
    onViewChange,
    onClose,
    changeAddress,
  } = useContext(AddressDropdownContext);

  const activeAddress = useMemo(
    () => findAddressByAddressKey(initialAddresses, addressKey),
    [addressKey, initialAddresses],
  );

  const addresses = useMemo(
    () => (activeAddress ? [activeAddress, ...without(initialAddresses, activeAddress)] : initialAddresses),
    [initialAddresses, activeAddress],
  );

  const isOpen = !!addressView;
  const noAddressOnMenuPage =
    !addressKey && isMenuPage && (!deliveryMethod || deliveryMethod === DeliveryMethods.DELIVERY);

  const shouldShowWarning = noAddressOnMenuPage && addressView === AddressViewValuesMap.MAIN;

  const isAddingFirstAddress = noAddressOnMenuPage && addressView === AddressViewValuesMap.ADD;

  const eligibleToAddCompanyAddress = userData?.userEnabledFeatures?.includes('AppendCompanyAddress');

  if (!isGoogleMapReady) {
    return null;
  }

  const ButtonTitle = (
    <>
      <ButtonText
        activeAddress={activeAddress}
        isAddingFirstAddress={isAddingFirstAddress}
      />

      {isMaxMobile && <TitleText addressView={addressView} />}

      <CloseAddress open={addressView && isMaxMobile} onClick={onClose} alt={t('back')} />
    </>
  );

  const startIcon = (
    <>
      {isMinLargeMobile && <NewLocationIcon />}
    </>
  );

  return (
    <Root isOpen={isOpen}>
      <StyledDropdownContainer
        onDropdownClick={toggleDropdown}
        closeDropdown={onClose}
        isOpen={isOpen}
        startIcon={startIcon}
        showArrow={(addressView === null && isMaxMobile) || !isMaxMobile}
        buttonTitle={ButtonTitle}
        isMaxLargeMobile={isMaxLargeMobile}
      >
        {isOpen && (
          <AddressContentWrapper>
            <AddressContentBody>
              {isMaxLargeMobile && (
                <StyledMobileStandaloneHeader
                  // @ts-expect-error AddressDropdownContext should be rewritten to ts
                  title={t(addressStandaloneTitlesMap[addressView])}
                  onBackClick={toggleDropdown}
                />
              )}
              {shouldShowWarning && <Warning>{t('add_delivery_address_to_continue')}</Warning>}
              {addressView === AddressViewValuesMap.MAIN && (
                // @ts-expect-error MainBlock should rewritten to ts
                <MainBlock
                  {...{
                    changeAddress,
                    onSubmitEditedAddress,
                    onViewChange,
                    addresses,
                    activeAddress,
                    addressView,
                    eligibleToAddCompanyAddress,
                    showNoAddressWarning: shouldShowWarning,
                    hideCurrentLocation: false,
                    hideEditButton: false,
                  }}
                />
              )}

              {addressView === AddressViewValuesMap.ADD && (
                // @ts-expect-error UpsertLocalAddressBlock should rewritten to ts
                <UpsertLocalAddressBlock
                  {...{
                    buttonText: t('add'),
                    titleText: t(isAddingFirstAddress ? 'where_to_send_the_food' : 'add_new_address'),
                    subTitleText: isAddingFirstAddress
                      ? t('excellent_choice_insert_delivery_address_to_continue')
                      : null,
                    addresses,
                    changeAddress,
                    onSubmit: handleSubmitLocalAddress,
                    onViewChange,
                  }}
                />
              )}

              {addressView === AddressViewValuesMap.EDIT_LOCAL && (
                <UpsertLocalAddressBlock
                  {...{
                    editedAddress,
                    buttonText: t('save_changes'),
                    titleText: t('edit_address'),
                    subTitleText: t('so_we_will_be_able_to_show_you_the_best_results'),
                    addresses,
                    changeAddress,
                    onSubmit: handleSubmitLocalAddress,
                    onViewChange,
                  }}
                />
              )}

              {addressView === AddressViewValuesMap.EDIT && (
                <EditRemoteAddressBlock
                  addresses={addresses}
                  editedAddress={editedAddress}
                  onViewChange={onViewChange}
                  editing
                />
              )}

              {addressView === AddressViewValuesMap.ADD_COMPANY_ADDRESS && (
                <AddCompanyAddressBlock onViewChange={onViewChange} addCompanyAddress={handleAddCompanyAddress} />
              )}
            </AddressContentBody>
          </AddressContentWrapper>
        )}
      </StyledDropdownContainer>
    </Root>
  );
};

export default memo(AddressDropdown);
